import { BRAND, pageTitle } from '@palaccount/config';
import type { SubscriptionView, UsageMeter } from '@palaccount/contracts';
import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '@/api';
import { AdminTabs } from '@/features/admin/admin-tabs';
import { Card, CardContent } from '@/components/ui/card';
import { throwApiError } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

/**
 * ADM-07 — SUBSCRIPTION & BILLING, the customer's side (§15.9).
 *
 * ── WHAT THIS SCREEN CANNOT DO, AND WHY THAT IS THE DESIGN ─────────────────
 *
 * There is no **Switch plan**, no **Manage payment method**, and no add-on
 * toggle. Owner ruling R21 gives a company exactly two ways to get access — an
 * online card payment, or an operator grant — and every control §6 lists would
 * be a THIRD, controlled by the customer. The operator console already owns
 * granting and extending (PART 22 §22.5); the online route is closed until
 * DECISION.md §8.7 names a payment provider.
 *
 * The absence is enforced far below this file: `app_user` holds SELECT and
 * nothing else on `tenant_subscriptions`, so the company's own credential
 * cannot extend its own subscription however this screen were rewritten.
 *
 * WHAT IS LEFT IS STILL THE THING THE CUSTOMER ASKED FOR — §1's own sentence
 * begins "see what you pay PalAccount, WHAT YOU ARE USING". Until this screen
 * existed a company could not see its plan, its renewal date, which modules it
 * had bought, or how many of its seats were gone. The last of those is the one
 * that bites, and it is now a real number: BR-ADM-05-12 is enforced, so the
 * meter and the refusal on the Users screen count the same people.
 *
 * ── THE OWNER ALONE ────────────────────────────────────────────────────────
 *
 * §9: "The Owner alone (BR-PERM-05, BR-PERM-69). Not the Administrator role,
 * not a custom role, and NOT GRANTABLE THROUGH THE PERMISSION GRID." The route
 * guard checks the flag (`route-permissions.ts`) and the server refuses
 * everybody else before it reads a row — this component is never the check.
 */

function useSubscription() {
  return useQuery({
    queryKey: ['subscription'] as const,
    queryFn: async () => {
      const res = await api.subscription.view();
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
  });
}

export function BillingPage() {
  const { t } = useTranslation();
  const { data, isPending, isError, error } = useSubscription();

  useEffect(() => {
    document.title = pageTitle(t('billing.title'));
  }, [t]);

  return (
    <div className="grid gap-4">
      <AdminTabs />

      {isPending ? (
        <p className="py-10 text-center text-sm text-muted-foreground">{t('shell.loading')}</p>
      ) : null}

      {isError ? (
        <p role="alert" className="text-sm text-destructive">
          {(error as Error).message}
        </p>
      ) : null}

      {data ? (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <PlanCard view={data} />
            <UsageCard meters={data.meters} />
          </div>
          <ModulesCard modules={data.modules} />
          <HistoryCard />
        </>
      ) : null}
    </div>
  );
}

/**
 * §4's plan card — "the only place in the whole product with a colour gradient
 * background — brand blue running into its darker shade, with white text".
 * Built from the accent variable rather than a hard-coded blue, so ADM-08's
 * accent choice recolours it like everything else (BR-ADM-08-09).
 */
function PlanCard({ view }: { view: SubscriptionView }) {
  const { t } = useTranslation();

  // Solid --primary rather than the old `from-primary to-primary/70`
  // gradient: white ink on the /70 corner measured 2.99:1 (sRGB) in light and
  // no gradient stop below /100 clears 4.5, so the wash was the whole defect.
  // The accent variable still recolours the card (BR-ADM-08-09).
  return (
    <Card className="overflow-hidden border-0 bg-primary text-primary-foreground">
      <CardContent className="grid gap-2 py-6">
        <div className="text-xs font-medium uppercase tracking-widest">
          {t('billing.currentPlan')}
        </div>
        {/* 17.5.2 — the plan is free text. Shown as stored, never matched
            against a price list this build does not have. */}
        <div className="text-2xl font-bold">{view.plan ?? t('billing.noPlan')}</div>

        {view.state === 'current' && view.periodEnd ? (
          <div className="text-sm">
            {t('billing.renews', { date: view.periodEnd })}
          </div>
        ) : null}
        {view.state === 'expired' && view.periodEnd ? (
          <div className="text-sm font-medium">{t('billing.expiredOn', { date: view.periodEnd })}</div>
        ) : null}
        {view.state === 'none' ? <div className="text-sm">{t('billing.stateNone')}</div> : null}

        {/*
          HOW they got it, and therefore how they renew it. R21's two routes
          need two different conversations, and a customer whose access came
          from a phone call must not be left looking for a card on file that
          was never there. This replaces §6's Manage payment method button,
          which has nothing behind it (DECISION.md §8.7).
        */}
        {view.grantedVia ? (
          <div className="mt-2 rounded-md bg-black/15 px-3 py-2 text-xs leading-relaxed">
            {t(`billing.grantedVia.${view.grantedVia}`)}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * §4's usage card. Three meters, not §5's four — a tenant IS a company in this
 * build (R19 replaced ADM-09 with the operator console, and a customer cannot
 * create a second company), so a Companies meter would read `1 / 1` for ever.
 */
function UsageCard({ meters }: { meters: readonly UsageMeter[] }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="grid gap-4 py-6">
        <h2 className="text-sm font-semibold">{t('billing.usageTitle')}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {meters.map((meter) => (
            <Meter key={meter.id} meter={meter} />
          ))}
        </div>
        {/* BR-ADM-07-07 — "a meter that is full must not stop the company
            working. It must stop the NEXT THING being added." Said out loud,
            because a red bar reads as an outage otherwise. */}
        <p className="text-xs text-muted-foreground">{t('billing.meterNote')}</p>
      </CardContent>
    </Card>
  );
}

function Meter({ meter }: { meter: UsageMeter }) {
  const { t } = useTranslation();
  const pct =
    meter.limit && meter.limit > 0 ? Math.min(100, (meter.used / meter.limit) * 100) : null;

  // BR-ADM-07-06's bands, decided on the SERVER and sent as a word. Colour is
  // never the only signal (§21.17): the figure beside the bar says the same.
  const fill =
    meter.band === 'full' ? 'bg-destructive' : meter.band === 'warning' ? 'bg-amber-500' : 'bg-primary';

  return (
    <div className="grid gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs text-muted-foreground">{t(`billing.meter.${meter.id}`)}</span>
        <span className="text-sm font-semibold tabular-nums">
          {meter.limit === null
            ? t(`billing.meterUsed.${meter.id}`, { used: meter.used })
            : `${meter.used} / ${meter.limit}`}
        </span>
      </div>
      {pct === null ? (
        // No published allowance: a figure, and deliberately no bar. A bar
        // needs a denominator, and inventing one would be inventing a plan.
        <p className="text-[11px] text-muted-foreground">{t('billing.noAllowance')}</p>
      ) : (
        <div
          className="h-2 overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuenow={meter.used}
          aria-valuemin={0}
          aria-valuemax={meter.limit ?? undefined}
          aria-label={t(`billing.meter.${meter.id}`)}
        >
          <div className={cn('h-full rounded-full', fill)} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

/**
 * §5 fields 5–6 — the add-ons, which §5 itself calls "a reflection rather than
 * a control", and a reflection is exactly what they are here.
 *
 * BOTH HALVES ARE DRAWN, because `EntitlementsGuard` needs both and they can
 * disagree: `sold` is what the period covers, `enabled` is the console's
 * switch. A module sold and then switched off is a support action, and a card
 * that showed one boolean could not explain it.
 */
function ModulesCard({ modules }: { modules: SubscriptionView['modules'] }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="grid gap-3 py-6">
        <h2 className="text-sm font-semibold">{t('billing.modulesTitle')}</h2>
        <p className="text-xs text-muted-foreground">{t('billing.modulesNote')}</p>
        <ul className="grid gap-2">
          {modules.map((row) => {
            const on = row.sold && row.enabled;
            return (
              <li
                key={row.module}
                className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2"
              >
                <span className="me-auto text-sm font-medium">
                  {t(`billing.module.${row.module}`)}
                </span>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-[11px] font-medium',
                    on
                      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                      : 'bg-muted text-muted-foreground',
                  )}
                >
                  {on ? t('billing.moduleOn') : t('billing.moduleOff')}
                </span>
                {/* What each one costs the company if it is off — §5's own
                    table, which says the three do NOT hide the same amount and
                    "a build must not treat them alike". */}
                <span className="w-full text-[11px] text-muted-foreground">
                  {t(`billing.moduleEffect.${row.module}`)}
                </span>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

/**
 * §4's billing history, and the honest version of it.
 *
 * There is no billing document anywhere in the platform — no table, no
 * `PAY-YYYY-MM` numbering, no payment record. §7 says as much itself ("Only
 * Paid exists in the product") and EC-15-19 records that the other three
 * statuses "are needed and missing". Drawing an empty table with four column
 * headings would imply rows are coming; saying where the invoices actually
 * are is more use.
 *
 * The sentence is §12's, VERBATIM and required: "the connection to the
 * accounts is a sentence a build must add to the screen".
 */
function HistoryCard() {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="grid gap-2 py-6">
        <h2 className="text-sm font-semibold">{t('billing.historyTitle')}</h2>
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>{t('billing.historyNone')}</span>
        </p>
        {/* §12's required sentence, verbatim. R20: the product name is one
            constant, never a translated string. */}
        <p className="text-sm text-muted-foreground">
          {t('billing.billsUnderPurchases', { brand: BRAND.name })}
        </p>
      </CardContent>
    </Card>
  );
}
